import React, { useState, useEffect } from 'react';
import { Trophy, Lock, Star, Flame, Droplet, CheckCircle, Dumbbell, Brain, Heart } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';

/**
 * BadgesDisplay - Shows all badges (earned and locked)
 * 
 * Features:
 * - Grid display of all available badges
 * - Visual distinction between earned and locked
 * - Progress indicators for streak-based badges
 * - Celebration animation for newly earned
 */
const BadgesDisplay = ({ showAsModal = false, onClose }) => {
  const { theme } = useTheme();
  const { t } = useLocale();
  const [badges, setBadges] = useState({ earned: [], available: {} });
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [streaks, setStreaks] = useState({});

  useEffect(() => {
    fetchBadges();
    fetchStreaks();
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/streaks/badges`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBadges({
          earned: data.badges || [],
          available: data.available_badges || {}
        });
      }
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStreaks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/streaks`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStreaks(data.streaks || {});
      }
    } catch (error) {
      console.error('Failed to fetch streaks:', error);
    }
  };

  const getStreakIcon = (type) => {
    const icons = {
      water: <Droplet className="w-6 h-6" />,
      tasks: <CheckCircle className="w-6 h-6" />,
      workout: <Dumbbell className="w-6 h-6" />,
      mood: <Brain className="w-6 h-6" />,
      login: <Star className="w-6 h-6" />,
      sleep: <Heart className="w-6 h-6" />
    };
    return icons[type] || <Trophy className="w-6 h-6" />;
  };

  const getBadgeProgress = (badge) => {
    if (badge.special) return null;
    const currentStreak = streaks[badge.streak_type]?.current || 0;
    const target = badge.days;
    return {
      current: currentStreak,
      target,
      percentage: Math.min((currentStreak / target) * 100, 100)
    };
  };

  const isBadgeEarned = (badgeId) => {
    return badges.earned.some(b => b.badge_id === badgeId || b.id === badgeId);
  };

  // Group badges by category
  const groupedBadges = Object.entries(badges.available).reduce((acc, [id, badge]) => {
    const category = badge.streak_type || 'special';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ id, ...badge });
    return acc;
  }, {});

  const categoryLabels = {
    water: { label: '💧 Hydration', color: '#3B82F6' },
    tasks: { label: '✅ Productivity', color: '#10B981' },
    workout: { label: '💪 Fitness', color: '#F59E0B' },
    mood: { label: '🧠 Mindfulness', color: '#8B5CF6' },
    login: { label: '⭐ Dedication', color: '#6EDBC8' },
    special: { label: '🏆 Special', color: '#F97316' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-gray-200 rounded-full"
          style={{ borderTopColor: theme?.colors?.primary }} />
      </div>
    );
  }

  const content = (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="flex items-center justify-between px-4 py-3 rounded-xl"
        style={{ backgroundColor: `${theme?.colors?.primary}10` }}>
        <div className="flex items-center gap-3">
          <Trophy className="w-8 h-8" style={{ color: theme?.colors?.primary }} />
          <div>
            <p className="text-sm text-gray-500">{t('home.badgesEarned') || 'Badges Earned'}</p>
            <p className="text-2xl font-bold" style={{ color: theme?.colors?.primary }}>
              {badges.earned.length} / {Object.keys(badges.available).length}
            </p>
          </div>
        </div>
        {badges.earned.length >= 5 && (
          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm">
            <Flame className="w-4 h-4" />
            <span>{t('home.collector') || 'Collector'}</span>
          </div>
        )}
      </div>

      {/* Badge Categories */}
      {Object.entries(groupedBadges).map(([category, categoryBadges]) => (
        <div key={category} className="space-y-3">
          <h3 className="text-sm font-medium text-gray-600 flex items-center gap-2">
            {categoryLabels[category]?.label || category}
          </h3>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {categoryBadges.map((badge) => {
              const earned = isBadgeEarned(badge.id);
              const progress = getBadgeProgress(badge);
              
              return (
                <button
                  key={badge.id}
                  onClick={() => setSelectedBadge(badge)}
                  className={`relative p-3 rounded-xl border-2 transition-all duration-200 ${
                    earned 
                      ? 'border-amber-300 bg-amber-50 shadow-sm hover:shadow-md' 
                      : 'border-gray-200 bg-gray-50 opacity-60 hover:opacity-80'
                  }`}
                >
                  {/* Badge Icon */}
                  <div className={`text-3xl text-center mb-1 ${!earned && 'grayscale'}`}>
                    {badge.icon}
                  </div>
                  
                  {/* Badge Name */}
                  <p className={`text-xs text-center truncate ${
                    earned ? 'text-gray-700 font-medium' : 'text-gray-500'
                  }`}>
                    {badge.name}
                  </p>
                  
                  {/* Progress Bar (for unearned streak badges) */}
                  {!earned && progress && (
                    <div className="mt-2">
                      <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${progress.percentage}%`,
                            backgroundColor: categoryLabels[category]?.color || theme?.colors?.primary
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-400 text-center mt-1">
                        {progress.current}/{progress.target}
                      </p>
                    </div>
                  )}
                  
                  {/* Lock Icon for unearned */}
                  {!earned && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center">
                      <Lock className="w-3 h-3 text-white" />
                    </div>
                  )}
                  
                  {/* Checkmark for earned */}
                  {earned && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <div 
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedBadge(null)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className={`text-6xl mb-4 ${!isBadgeEarned(selectedBadge.id) && 'grayscale'}`}>
                {selectedBadge.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {selectedBadge.name}
              </h3>
              <p className="text-gray-600 mb-4">
                {selectedBadge.description}
              </p>
              
              {isBadgeEarned(selectedBadge.id) ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Earned!</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {getBadgeProgress(selectedBadge) && (
                    <>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${getBadgeProgress(selectedBadge).percentage}%`,
                            backgroundColor: theme?.colors?.primary
                          }}
                        />
                      </div>
                      <p className="text-sm text-gray-500">
                        {getBadgeProgress(selectedBadge).current} / {getBadgeProgress(selectedBadge).target} days
                      </p>
                    </>
                  )}
                  <p className="text-sm text-gray-400">Keep going to unlock!</p>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setSelectedBadge(null)}
              className="w-full mt-6 py-3 rounded-xl font-medium text-white"
              style={{ backgroundColor: theme?.colors?.primary }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (showAsModal) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Your Badges</h2>
            <button 
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
          <div className="p-6">
            {content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Badges</h2>
      {content}
    </div>
  );
};

export default BadgesDisplay;
